/**
 * /e2e-tests — Admin-only end-to-end trade-cycle test harness UI (Stage 2).
 *
 * Re-design from the first cut:
 *   - Full-width page (was 1200px max-width — too narrow on modern displays)
 *   - Sticky progress banner during a run showing "scenario X of Y · current: …"
 *   - Every scenario row has an "About" preamble (description + production code
 *     reference) before its step log.
 *   - Per-step timestamps in HH:MM:SS.mmm format
 *   - Pass / fail step lines visually distinguished
 *   - Run starts auto-expand the currently-running row so the user can read
 *     steps live without manual clicks.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Stack, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Collapse,
  IconButton, Alert as MuiAlert, Divider, useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingIcon from '@mui/icons-material/Pending';
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Dialog, DialogTitle, DialogContent, DialogActions, Link } from '@mui/material';
import { getGlobalSocket } from '../hooks/useWebSocket';
import {
  e2eTestService, E2EScenarioMeta, E2ERun, E2EScenarioResultRow,
} from '../services/e2eTest.service';
import toast from 'react-hot-toast';

interface StepEntry {
  ts: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

interface LiveScenarioState {
  status: 'pending' | 'running' | 'pass' | 'fail' | 'skip';
  startedAt?: number;
  finishedAt?: number;
  durationMs?: number;
  failureReason?: string;
  description?: string;
  productionPath?: string;
  steps: StepEntry[];
}

const initialState = (): LiveScenarioState => ({ status: 'pending', steps: [] });

const fmtMs = (ms?: number | null) => (ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`);
const fmtTs = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—');

/** HH:MM:SS.mmm relative to wall clock — used in step logs so each
 *  line carries its own occurrence time. */
const fmtClock = (ts: number) => {
  const d = new Date(ts);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const E2ETestsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Admin gate via the same pattern UserSettings uses — try listScenarios;
  // if it 403s we render the access-denied banner.
  const [adminCheck, setAdminCheck] = useState<'pending' | 'ok' | 'denied'>('pending');

  const [scenarios, setScenarios] = useState<E2EScenarioMeta[]>([]);
  const [history, setHistory] = useState<E2ERun[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [runStarting, setRunStarting] = useState(false);
  const [runActive, setRunActive] = useState(false);
  const [live, setLive] = useState<Record<string, LiveScenarioState>>({});
  const [runSummary, setRunSummary] = useState<{ pass: number; fail: number; skip: number; durationMs?: number; status?: string } | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  /** Progress info live during a run — set on scenario_started, cleared on run_finished. */
  const [progress, setProgress] = useState<{ ord: number; ofTotal: number; scenarioId: string; name?: string } | null>(null);

  const [historyDetail, setHistoryDetail] = useState<{ run: E2ERun; scenarios: E2EScenarioResultRow[] } | null>(null);

  /** Help dialog visibility. */
  const [helpOpen, setHelpOpen] = useState(false);

  /** Diff state — when set, we render a panel showing scenarios that
   *  changed status between two runs. */
  const [diffView, setDiffView] = useState<{
    current: { run: E2ERun; scenarios: E2EScenarioResultRow[] };
    previous: { run: E2ERun; scenarios: E2EScenarioResultRow[] };
  } | null>(null);

  const attachedRef = useRef<ReturnType<typeof getGlobalSocket>>(null);
  /** Polling interval handle while a run is active. Polling is the
   *  PRIMARY source of truth (WS is a fast-path bonus when available).
   *  Admin-only page so the 1 s cadence overhead is negligible. */
  const pollRef = useRef<number | null>(null);
  /** The runId we're currently polling for so a stale interval that
   *  fires after the user starts a new run doesn't update wrong state. */
  const activeRunRef = useRef<string | null>(null);

  // ── Initial load ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await e2eTestService.listScenarios();
        if (cancelled) return;
        setScenarios(list);
        setLive(Object.fromEntries(list.map(s => [s.id, initialState()])));
        setAdminCheck('ok');
        const runs = await e2eTestService.listRuns();
        if (!cancelled) setHistory(runs);
      } catch (e: any) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 403) setAdminCheck('denied');
        else {
          setAdminCheck('denied');
          toast.error(`Failed to load E2E scenarios: ${e?.message || 'unknown'}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── WebSocket subscription on the SHARED global socket ──────────────────
  useEffect(() => {
    if (adminCheck !== 'ok') return;
    let detached = false;
    let pollTimer: number | null = null;

    const onRunStarted = (data: any) => {
      if (detached) return;
      setRunId(data.runId);
      setRunActive(true);
      setRunSummary(null);
      setProgress({ ord: 0, ofTotal: data.totalScenarios, scenarioId: '' });
      setLive(prev => {
        const next: Record<string, LiveScenarioState> = {};
        for (const k of Object.keys(prev)) next[k] = initialState();
        return next;
      });
      // Collapse all rows so the auto-expand-current behaviour reads cleanly.
      setExpanded({});
    };

    const onScenarioStarted = (data: any) => {
      if (detached) return;
      setProgress({ ord: data.ord + 1, ofTotal: data.ofTotal || scenarios.length, scenarioId: data.scenarioId, name: data.name });
      setLive(prev => ({
        ...prev,
        [data.scenarioId]: {
          ...(prev[data.scenarioId] || initialState()),
          status: 'running',
          startedAt: data.ts,
          steps: [],
          description: data.description,
          productionPath: data.productionPath,
        },
      }));
      // Auto-expand the currently-running scenario so the user sees the step log fill in real-time.
      setExpanded(prev => ({ ...prev, [data.scenarioId]: true }));
    };

    const onScenarioStep = (data: any) => {
      if (detached) return;
      setLive(prev => {
        const cur = prev[data.scenarioId] || initialState();
        return {
          ...prev,
          [data.scenarioId]: {
            ...cur,
            steps: [...cur.steps, { ts: data.ts || Date.now(), level: data.level || 'info', message: data.step }],
          },
        };
      });
    };

    const onScenarioFinished = (data: any) => {
      if (detached) return;
      setLive(prev => {
        const cur = prev[data.scenarioId] || initialState();
        return {
          ...prev,
          [data.scenarioId]: {
            ...cur,
            status: data.status,
            durationMs: data.durationMs,
            finishedAt: data.ts,
            failureReason: data.failureReason,
          },
        };
      });
      // Keep failed scenarios expanded; collapse passed ones so attention follows the cursor.
      if (data.status === 'pass') {
        setExpanded(prev => ({ ...prev, [data.scenarioId]: false }));
      } else {
        setExpanded(prev => ({ ...prev, [data.scenarioId]: true }));
      }
    };

    const onRunFinished = (data: any) => {
      if (detached) return;
      setRunActive(false);
      setProgress(null);
      setRunSummary({ pass: data.pass, fail: data.fail, skip: data.skip, durationMs: data.durationMs, status: data.status });
      e2eTestService.listRuns().then(setHistory).catch(() => {});
    };

    const attach = () => {
      const sock = getGlobalSocket();
      if (!sock) {
        pollTimer = window.setTimeout(attach, 300);
        return;
      }
      attachedRef.current = sock;
      sock.on('e2e:run_started',      onRunStarted);
      sock.on('e2e:scenario_started', onScenarioStarted);
      sock.on('e2e:scenario_step',    onScenarioStep);
      sock.on('e2e:scenario_finished', onScenarioFinished);
      sock.on('e2e:run_finished',     onRunFinished);
    };

    attach();

    return () => {
      detached = true;
      if (pollTimer != null) window.clearTimeout(pollTimer);
      const sock = attachedRef.current;
      if (sock) {
        sock.off('e2e:run_started',      onRunStarted);
        sock.off('e2e:scenario_started', onScenarioStarted);
        sock.off('e2e:scenario_step',    onScenarioStep);
        sock.off('e2e:scenario_finished', onScenarioFinished);
        sock.off('e2e:run_finished',     onRunFinished);
      }
      attachedRef.current = null;
    };
  }, [adminCheck, scenarios.length]);

  /**
   * Poll the run detail endpoint every 1 s, applying scenario states
   * to the live UI. Stops when run.status is 'passed' or 'failed'.
   *
   * Polling is the PRIMARY update mechanism — the WS handlers above
   * are kept as a fast-path bonus when the WS connection happens to
   * work for the session. This means the page stays responsive even
   * when WS routing is broken (e.g. behind a proxy that doesn't
   * forward socket.io upgrades cleanly, or after a session whose JWT
   * has been replaced by cookie-only auth).
   */
  const startPolling = (runIdToPoll: string) => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    activeRunRef.current = runIdToPoll;

    const applyDetail = async () => {
      if (activeRunRef.current !== runIdToPoll) return;
      try {
        const { run, scenarios: rows } = await e2eTestService.getRun(runIdToPoll);

        // Reset all rows to pending each pass, then overlay persisted rows.
        setLive(prev => {
          const next: Record<string, LiveScenarioState> = {};
          for (const k of Object.keys(prev)) next[k] = { ...prev[k] };
          for (const row of rows) {
            const startedTs = row.startedAt ? new Date(row.startedAt).getTime() : undefined;
            const finishedTs = row.finishedAt ? new Date(row.finishedAt).getTime() : undefined;
            next[row.scenarioId] = {
              ...(next[row.scenarioId] || initialState()),
              status: row.status as any,
              startedAt: startedTs,
              finishedAt: finishedTs,
              durationMs: row.durationMs,
              failureReason: row.failureReason || undefined,
              steps: (row.stepsLog || []).map(s => ({ ts: s.ts, level: s.level, message: s.message })),
            };
          }
          return next;
        });

        // Compute progress from the persisted count.
        setProgress({
          ord: Math.min(rows.length + (run.status === 'running' ? 1 : 0), run.totalScenarios),
          ofTotal: run.totalScenarios,
          scenarioId: '',
        });

        if (run.status !== 'running') {
          // Run finished — apply summary, stop polling, refresh history.
          setRunActive(false);
          setProgress(null);
          setRunSummary({
            pass: run.passCount, fail: run.failCount, skip: run.skipCount,
            durationMs: run.durationMs || undefined,
            status: run.status,
          });
          e2eTestService.listRuns().then(setHistory).catch(() => {});
          if (pollRef.current != null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
          }
          activeRunRef.current = null;
        }
      } catch {
        // Transient network blip — keep polling.
      }
    };

    // Fire once immediately for fast first paint, then on interval.
    void applyDetail();
    pollRef.current = window.setInterval(applyDetail, 1000);
  };

  // Cleanup poll on unmount.
  useEffect(() => () => {
    if (pollRef.current != null) window.clearInterval(pollRef.current);
    pollRef.current = null;
    activeRunRef.current = null;
  }, []);

  /** Browser-side JSON download of a run + its scenarios. Filename uses
   *  the run's shortId + IST date so the user can save several side-by-side. */
  const downloadRunJson = (run: E2ERun, scenarios: E2EScenarioResultRow[]) => {
    const payload = { run, scenarios };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dt = new Date(run.startedAt);
    const stamp = `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, '0')}${String(dt.getDate()).padStart(2, '0')}-${String(dt.getHours()).padStart(2, '0')}${String(dt.getMinutes()).padStart(2, '0')}`;
    a.download = `e2e-run-${run.id.slice(0, 8)}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /** Open the diff panel for one history row vs the NEXT-older row. */
  const openDiffAgainstPrevious = async (currentIdx: number) => {
    if (currentIdx >= history.length - 1) {
      toast.error('No previous run to compare against.');
      return;
    }
    const currentRunMeta = history[currentIdx];
    const prevRunMeta = history[currentIdx + 1];
    try {
      const [cur, prev] = await Promise.all([
        e2eTestService.getRun(currentRunMeta.id),
        e2eTestService.getRun(prevRunMeta.id),
      ]);
      setDiffView({ current: cur, previous: prev });
    } catch {
      toast.error('Failed to load runs for diff');
    }
  };

  /** Compute scenario-level diff. */
  const computeDiff = (
    current: E2EScenarioResultRow[],
    previous: E2EScenarioResultRow[],
  ): Array<{ scenarioId: string; curStatus?: string; prevStatus?: string; kind: 'unchanged' | 'changed' | 'added' | 'removed'; curDuration?: number; prevDuration?: number }> => {
    const curMap = new Map(current.map(s => [s.scenarioId, s]));
    const prevMap = new Map(previous.map(s => [s.scenarioId, s]));
    const allIds = new Set<string>();
    curMap.forEach((_, k) => allIds.add(k));
    prevMap.forEach((_, k) => allIds.add(k));
    const out: Array<any> = [];
    allIds.forEach(id => {
      const c = curMap.get(id), p = prevMap.get(id);
      if (c && !p) out.push({ scenarioId: id, curStatus: c.status, kind: 'added', curDuration: c.durationMs });
      else if (!c && p) out.push({ scenarioId: id, prevStatus: p.status, kind: 'removed', prevDuration: p.durationMs });
      else if (c && p) {
        const kind = c.status === p.status ? 'unchanged' : 'changed';
        out.push({ scenarioId: id, curStatus: c.status, prevStatus: p.status, kind, curDuration: c.durationMs, prevDuration: p.durationMs });
      }
    });
    // Sort: changed first, then added/removed, then unchanged.
    const rank = { changed: 0, added: 1, removed: 2, unchanged: 3 };
    out.sort((a, b) => rank[a.kind as keyof typeof rank] - rank[b.kind as keyof typeof rank] || a.scenarioId.localeCompare(b.scenarioId));
    return out;
  };

  /** Aggregate stats over the visible history list. */
  const historyStats = useMemo(() => {
    if (history.length === 0) return null;
    let totalPass = 0, totalFail = 0, totalDuration = 0, completedRuns = 0;
    for (const r of history) {
      if (r.status === 'running') continue;
      totalPass += r.passCount;
      totalFail += r.failCount;
      if (r.durationMs != null) { totalDuration += r.durationMs; completedRuns++; }
    }
    const totalScenarios = totalPass + totalFail;
    return {
      runCount: completedRuns,
      passRate: totalScenarios > 0 ? Math.round((totalPass / totalScenarios) * 100) : 0,
      avgDurationMs: completedRuns > 0 ? Math.round(totalDuration / completedRuns) : 0,
      latestPassCount: history[0]?.passCount ?? 0,
      latestFailCount: history[0]?.failCount ?? 0,
    };
  }, [history]);

  const onRun = async () => {
    if (runActive || runStarting) return;
    setRunStarting(true);
    try {
      const { runId, totalScenarios } = await e2eTestService.startRun();
      setRunId(runId);
      setRunActive(true);
      setRunSummary(null);
      setProgress({ ord: 0, ofTotal: totalScenarios, scenarioId: '' });
      // Reset all rows to pending so the user sees a clean slate.
      setLive(prev => {
        const next: Record<string, LiveScenarioState> = {};
        for (const k of Object.keys(prev)) next[k] = initialState();
        return next;
      });
      setExpanded({});
      // Start polling. WS (if working) will also push updates — both
      // converge on the same state.
      startPolling(runId);
    } catch (e: any) {
      const code = e?.response?.data?.error?.code;
      if (code === 'E2E_RUN_IN_PROGRESS') toast.error('A run is already in progress on the server');
      else toast.error(`Failed to start run: ${e?.response?.data?.error?.message || e?.message}`);
    } finally {
      setRunStarting(false);
    }
  };

  const toggleExpand = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const grouped = useMemo(() => {
    const m = new Map<string, E2EScenarioMeta[]>();
    for (const s of scenarios) {
      const arr = m.get(s.group) || [];
      arr.push(s);
      m.set(s.group, arr);
    }
    return Array.from(m.entries());
  }, [scenarios]);

  const groupLabel = (g: string) =>
    g === 'L0' ? 'L0 — Harness self-test'
    : g === 'L' ? 'LONG — BTCUSD trade-cycle rules'
    : g === 'S' ? 'SHORT — ETHUSD trade-cycle rules'
    : g.startsWith('T') ? `${g} — Settings-tab driven`
    : g.startsWith('D') ? `${g} — Dashboard surface`
    : g.startsWith('X') ? 'X — Cross-cutting'
    : g;

  if (adminCheck === 'pending') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }
  if (adminCheck === 'denied') {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
        <MuiAlert severity="warning">
          This page is admin-only. If you should have access, ask the platform owner to set your role to admin.
        </MuiAlert>
      </Box>
    );
  }

  const progressPct = progress && progress.ofTotal > 0 ? Math.round((progress.ord / progress.ofTotal) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      {/* Sticky progress banner while a run is active */}
      {runActive && progress && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            mb: 2,
            p: 1.5,
            borderLeft: 4,
            borderColor: 'info.main',
            backgroundColor: isDark ? 'rgba(33, 150, 243, 0.12)' : 'rgba(33, 150, 243, 0.08)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label="RUNNING" color="info" size="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Scenario {progress.ord} of {progress.ofTotal}
              {progress.scenarioId && ` · ${progress.scenarioId} — ${progress.name || ''}`}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={progressPct} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>
              {progressPct}%
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Page header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            End-to-End Trade Cycle Tests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scenarios.length} scenario{scenarios.length === 1 ? '' : 's'} registered ·
            admin-only · all Delta calls mocked · spec: <code>docs/e2e-trade-cycle-test-plan.md</code>
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setHelpOpen(true)}
        >
          How does this work?
        </Button>
      </Box>

      {/* Aggregate stats card (R117 Stage 7 UX) */}
      {historyStats && historyStats.runCount > 0 && (
        <Paper sx={{ mb: 2, p: 1.5 }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TrendingUpIcon fontSize="small" color="primary" />
              <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7 }}>
                STATS (last {history.length} runs)
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">Pass rate:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: historyStats.passRate === 100 ? 'success.main' : historyStats.passRate >= 90 ? 'info.main' : 'warning.main' }}>
                {historyStats.passRate}%
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">Avg duration:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMs(historyStats.avgDurationMs)}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">Completed runs:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{historyStats.runCount}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="caption" color="text.secondary">Latest:</Typography>
              <Chip
                label={`${historyStats.latestPassCount} / ${historyStats.latestFailCount}`}
                size="small"
                color={historyStats.latestFailCount === 0 ? 'success' : 'error'}
                sx={{ height: 20 }}
              />
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Top controls + last-run summary */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ flex: 1, p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              disabled={runActive || runStarting}
              onClick={onRun}
            >
              {runActive ? 'Running…' : runStarting ? 'Starting…' : 'Run All Tests'}
            </Button>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Sequentially runs every registered scenario, streams live
                progress over WebSocket, and persists results.
              </Typography>
              {runId && (
                <Typography variant="caption" color="text.secondary">
                  Last Run ID: <code>{runId.slice(0, 8)}…</code>
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Last completed run
          </Typography>
          {runSummary ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Chip
                label={runSummary.status === 'passed' ? 'ALL PASS' : 'HAS FAILURES'}
                color={runSummary.status === 'passed' ? 'success' : 'error'}
                size="small"
              />
              <Typography variant="body2">
                {runSummary.pass} pass · {runSummary.fail} fail · {runSummary.skip} skip
              </Typography>
              <Typography variant="caption" color="text.secondary">{fmtMs(runSummary.durationMs)}</Typography>
            </Stack>
          ) : history.length > 0 ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Chip
                label={history[0].status === 'passed' ? 'ALL PASS' : history[0].status === 'failed' ? 'HAS FAILURES' : history[0].status.toUpperCase()}
                color={history[0].status === 'passed' ? 'success' : history[0].status === 'failed' ? 'error' : 'default'}
                size="small"
              />
              <Typography variant="body2">
                {history[0].passCount} pass · {history[0].failCount} fail · {history[0].skipCount} skip
              </Typography>
              <Typography variant="caption" color="text.secondary">{fmtMs(history[0].durationMs)}</Typography>
              <Typography variant="caption" color="text.secondary">· {fmtTs(history[0].startedAt)}</Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">No runs yet — click Run All Tests.</Typography>
          )}
        </Paper>
      </Stack>

      {/* Scenarios table */}
      <Paper sx={{ p: 0, mb: 3 }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Scenarios</Typography>
        </Box>
        {grouped.map(([group, items]) => (
          <Box key={group}>
            <Box sx={{ p: 1, px: 2, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                {groupLabel(group)} · {items.length} scenario{items.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {items.map(s => {
                  const st = live[s.id] || initialState();
                  const isOpen = !!expanded[s.id];
                  const rowBg =
                    st.status === 'pass'    ? (isDark ? 'rgba(76,175,80,0.05)' : 'rgba(76,175,80,0.04)')
                    : st.status === 'fail'  ? (isDark ? 'rgba(244,67,54,0.07)' : 'rgba(244,67,54,0.05)')
                    : st.status === 'running' ? (isDark ? 'rgba(33,150,243,0.07)' : 'rgba(33,150,243,0.05)')
                    : 'transparent';
                  return (
                    <React.Fragment key={s.id}>
                      <TableRow hover sx={{ cursor: 'pointer', backgroundColor: rowBg }} onClick={() => toggleExpand(s.id)}>
                        <TableCell sx={{ width: 40 }}>
                          <IconButton size="small">
                            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ width: 70 }}>
                          <Chip label={s.id} size="small" variant="outlined" sx={{ fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace' }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{s.name}</TableCell>
                        <TableCell sx={{ width: 140 }}>
                          {st.status === 'pending' && <Chip icon={<PendingIcon />}        label="Pending"  size="small" variant="outlined" />}
                          {st.status === 'running' && <Chip icon={<HourglassEmptyIcon />} label="Running…" size="small" color="info" />}
                          {st.status === 'pass'    && <Chip icon={<CheckCircleIcon />}    label="PASS"     size="small" color="success" />}
                          {st.status === 'fail'    && <Chip icon={<CancelIcon />}         label="FAIL"     size="small" color="error" />}
                          {st.status === 'skip'    && <Chip label="skip" size="small" />}
                        </TableCell>
                        <TableCell sx={{ width: 110 }}>
                          <Typography variant="caption" color="text.secondary">{fmtMs(st.durationMs)}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: 90 }}>
                          <Typography variant="caption" color="text.secondary">
                            {st.startedAt ? fmtClock(st.startedAt) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.025)' }}>
                              {/* About preamble — description + production path */}
                              {(st.description || s.name) && (
                                <Box sx={{ mb: 1.5, p: 1.25, borderLeft: 3, borderColor: 'info.main', bgcolor: isDark ? 'rgba(33,150,243,0.06)' : 'rgba(33,150,243,0.05)', borderRadius: '0 4px 4px 0' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.main', display: 'block', mb: 0.25 }}>
                                    ABOUT THIS SCENARIO
                                  </Typography>
                                  {st.description ? (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>{st.description}</Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontStyle: 'italic' }}>
                                      Description streams in when the scenario runs. Click Run All Tests to populate.
                                    </Typography>
                                  )}
                                  {st.productionPath && (
                                    <Typography variant="caption" color="text.secondary">
                                      Tests production code at: <code>{st.productionPath}</code>
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {st.failureReason && (
                                <MuiAlert severity="error" sx={{ mb: 1 }}>
                                  <strong>Failure reason:</strong> {st.failureReason}
                                </MuiAlert>
                              )}

                              {st.steps.length === 0 ? (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No steps captured yet. They will stream live as the scenario runs.
                                </Typography>
                              ) : (
                                <Box sx={{ maxHeight: 480, overflowY: 'auto', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.78rem' }}>
                                  {st.steps.map((step, i) => {
                                    const isErr  = step.level === 'error';
                                    const isWarn = step.level === 'warn';
                                    const color = isErr ? 'error.main' : isWarn ? 'warning.main' : 'text.primary';
                                    const bg    = isErr ? (isDark ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.05)') : 'transparent';
                                    return (
                                      <Box key={i} sx={{ display: 'flex', gap: 1, p: 0.4, backgroundColor: bg, borderRadius: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 95, opacity: 0.7 }}>
                                          {fmtClock(step.ts)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color, whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>
                                          {step.message}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}

                              {st.finishedAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                  Started {fmtClock(st.startedAt || st.finishedAt)} · Finished {fmtClock(st.finishedAt)} · Duration {fmtMs(st.durationMs)}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ))}
        {scenarios.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No scenarios registered.</Typography>
          </Box>
        )}
      </Paper>

      {/* Run history */}
      <Paper sx={{ p: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Run history (last 20)
          </Typography>
        </Box>
        {history.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No runs yet.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Pass / Fail / Skip</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Run ID</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((r, idx) => (
                <TableRow key={r.id} hover>
                  <TableCell>{fmtTs(r.startedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.status.toUpperCase()}
                      size="small"
                      color={r.status === 'passed' ? 'success' : r.status === 'failed' ? 'error' : r.status === 'running' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.passCount} / {r.failCount} / {r.skipCount}</Typography>
                  </TableCell>
                  <TableCell>{fmtMs(r.durationMs)}</TableCell>
                  <TableCell><code style={{ fontSize: 11 }}>{r.id.slice(0, 8)}…</code></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      onClick={async () => {
                        try { const d = await e2eTestService.getRun(r.id); setHistoryDetail(d); }
                        catch { toast.error('Failed to load run detail'); }
                      }}
                    >View</Button>
                    <Button
                      size="small"
                      disabled={idx >= history.length - 1}
                      startIcon={<CompareArrowsIcon fontSize="small" />}
                      onClick={() => openDiffAgainstPrevious(idx)}
                      title={idx >= history.length - 1 ? 'No older run to compare against' : 'Compare to next-older run'}
                    >Diff</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {historyDetail && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 1 }}>
              Run detail — {historyDetail.run.id.slice(0, 8)}…
            </Typography>
            <Chip
              label={historyDetail.run.status.toUpperCase()}
              size="small"
              color={historyDetail.run.status === 'passed' ? 'success' : historyDetail.run.status === 'failed' ? 'error' : 'default'}
            />
            <Typography variant="caption" color="text.secondary">
              {fmtTs(historyDetail.run.startedAt)} · {fmtMs(historyDetail.run.durationMs)} · {historyDetail.run.passCount} pass / {historyDetail.run.failCount} fail
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={() => downloadRunJson(historyDetail.run, historyDetail.scenarios)}
            >Download JSON</Button>
            <Button size="small" onClick={() => setHistoryDetail(null)}>Close</Button>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {historyDetail.scenarios.map(s => (
            <Box key={s.id} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={s.scenarioId} size="small" variant="outlined" sx={{ fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace' }} />
                <Chip
                  label={s.status === 'pass' ? 'PASS' : s.status === 'fail' ? 'FAIL' : s.status.toUpperCase()}
                  size="small"
                  color={s.status === 'pass' ? 'success' : s.status === 'fail' ? 'error' : 'default'}
                />
                <Typography variant="caption">{fmtMs(s.durationMs)}</Typography>
                <Typography variant="caption" color="text.secondary">· {fmtTs(s.startedAt)}</Typography>
              </Stack>
              {s.failureReason && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {s.failureReason}
                </Typography>
              )}
              {s.stepsLog?.length > 0 && (
                <Box sx={{ mt: 0.5, maxHeight: 360, overflowY: 'auto' }}>
                  {s.stepsLog.map((st, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, p: 0.3, fontSize: '0.75rem', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 95, opacity: 0.7 }}>
                        {fmtClock(st.ts)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: st.level === 'error' ? 'error.main' : st.level === 'warn' ? 'warning.main' : 'text.primary', whiteSpace: 'pre-wrap', flex: 1 }}>
                        {st.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* Help dialog — quick reference for what the harness tests and how reliable it is.
          Full reference lives in the App Guide; this is the short-form summary. */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon /> About the E2E Test Harness
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>What this page does.</strong> Each click of <strong>Run All Tests</strong> sequentially executes {scenarios.length} scenarios
            that verify the production code responsible for trade decisions, indicator math, voter classifications, settings
            validation, and trade-lifecycle state transitions. Results stream live, persist for the last 20 runs, and can be
            diffed scenario-by-scenario against any earlier run.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
            How data is collected
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Two distinct mechanisms power the suite:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 1.5, '& li': { mb: 0.5 } }}>
            <li>
              <Typography variant="body2">
                <strong>Hand-computed fixtures (38 of {scenarios.length} scenarios)</strong> — input values are fabricated in the scenario
                file and fed to the production function. Expected outputs are computed independently from the underlying math
                so the verification is rigorous. No DB, no broker, no network.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Real DB writes + mock broker (3 of {scenarios.length} scenarios)</strong> — inserts test Trade rows tagged with
                <code> isSimulation=true</code> to a sentinel E2E user, invokes the real production trade-monitor methods, and uses a
                <code> MockDeltaAdapter</code> that logs every broker call but NEVER hits real Delta. Cleanup runs in a
                <code> finally</code> block.
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
            Reliability — what to trust
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 1.5, '& li': { mb: 0.5 } }}>
            <li>
              <Typography variant="body2">
                <strong>HIGH</strong> trust on indicator math, voter rules, OverView scoring, Auto-ISL/Target math, settings
                clamps, BUY/SHORT/NEUTRAL classifications, historical-regression fixtures (e.g. SMA1's Round-70 protection).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>MEDIUM</strong> trust on trade-lifecycle state transitions (MFE, auto-trail activation, snooze) — these
                run against real Postgres but skip the candle-driven HA-trail SL computation because the mock instrument has no
                candle history.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>LOW</strong> trust (not covered) on real Delta order placement, frontend rendering, TradingView webhook
                routing, concurrent multi-user scenarios, long-running multi-day trade evolution.
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
            Using the suite as a pre-deploy gate
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The suite is <strong>necessary but not sufficient</strong>. Before a production deploy:
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 1.5, '& li': { mb: 0.5 } }}>
            <li><Typography variant="body2">Run the full suite — all-pass required.</Typography></li>
            <li><Typography variant="body2">Use the <strong>Diff</strong> button on the previous run to confirm zero scenarios changed status (added/removed scenarios should be intentional).</Typography></li>
            <li><Typography variant="body2">Manually smoke-test the new feature in staging or via Delta sandbox.</Typography></li>
            <li><Typography variant="body2">Deploy with a rollback path ready (previous Docker image preserved).</Typography></li>
            <li><Typography variant="body2">Monitor Logs + Telegram alerts for 24 hours.</Typography></li>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
            For the in-depth reference — runner mechanism, data-collection details, trust matrix by change type, adding new
            scenarios — see the App Guide.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<OpenInNewIcon />}
            component={Link}
            href="/app-guide#e2e-tests"
            target="_blank"
            rel="noopener"
            variant="outlined"
          >
            Open full App Guide section
          </Button>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Diff panel — scenario-level comparison between two runs (R117 Stage 7 UX) */}
      {diffView && (() => {
        const diff = computeDiff(diffView.current.scenarios, diffView.previous.scenarios);
        const changed = diff.filter(d => d.kind === 'changed');
        const added   = diff.filter(d => d.kind === 'added');
        const removed = diff.filter(d => d.kind === 'removed');
        const unchanged = diff.filter(d => d.kind === 'unchanged');
        return (
          <Paper sx={{ mt: 3, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 1 }}>
                Diff — {diffView.current.run.id.slice(0, 8)}…
                <Box component="span" sx={{ mx: 0.5, opacity: 0.5 }}>vs</Box>
                {diffView.previous.run.id.slice(0, 8)}…
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={`${changed.length} changed`} size="small" color={changed.length ? 'warning' : 'default'} />
              <Chip label={`${added.length} added`}     size="small" color={added.length ? 'info' : 'default'} />
              <Chip label={`${removed.length} removed`} size="small" color={removed.length ? 'info' : 'default'} />
              <Chip label={`${unchanged.length} same`}  size="small" />
              <Button size="small" onClick={() => setDiffView(null)}>Close</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Comparing {fmtTs(diffView.current.run.startedAt)} (current) vs {fmtTs(diffView.previous.run.startedAt)} (previous).
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {diff.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No scenarios in either run.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Previous</TableCell>
                    <TableCell>Current</TableCell>
                    <TableCell>Δ duration</TableCell>
                    <TableCell>Kind</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diff.map(row => {
                    const tint =
                      row.kind === 'changed'  ? (isDark ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.06)')
                      : row.kind === 'added'  ? (isDark ? 'rgba(33,150,243,0.06)' : 'rgba(33,150,243,0.04)')
                      : row.kind === 'removed' ? (isDark ? 'rgba(158,158,158,0.08)' : 'rgba(158,158,158,0.06)')
                      : 'transparent';
                    const delta = (row.curDuration != null && row.prevDuration != null) ? row.curDuration - row.prevDuration : null;
                    return (
                      <TableRow key={row.scenarioId} sx={{ backgroundColor: tint }}>
                        <TableCell>
                          <Chip label={row.scenarioId} size="small" variant="outlined" sx={{ fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace' }} />
                        </TableCell>
                        <TableCell>
                          {row.prevStatus ? (
                            <Chip
                              label={row.prevStatus === 'pass' ? 'PASS' : row.prevStatus === 'fail' ? 'FAIL' : row.prevStatus.toUpperCase()}
                              size="small"
                              color={row.prevStatus === 'pass' ? 'success' : row.prevStatus === 'fail' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          {row.curStatus ? (
                            <Chip
                              label={row.curStatus === 'pass' ? 'PASS' : row.curStatus === 'fail' ? 'FAIL' : row.curStatus.toUpperCase()}
                              size="small"
                              color={row.curStatus === 'pass' ? 'success' : row.curStatus === 'fail' ? 'error' : 'default'}
                            />
                          ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          {delta != null ? (
                            <Typography variant="caption" sx={{ color: Math.abs(delta) < 50 ? 'text.secondary' : delta > 0 ? 'warning.main' : 'success.main', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                              {delta > 0 ? '+' : ''}{delta}ms
                            </Typography>
                          ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, opacity: 0.7 }}>
                            {row.kind}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        );
      })()}
    </Box>
  );
};

export default E2ETestsPage;
